import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../hooks';
import { logout, selectUser } from '../store/slices/authSlice';
import { ThemeToggle } from '../components/ThemeToggle';
import LoadingSpinner from '../components/LoadingSpinner';
import apiClient from '../services/api';
import {
  UserSummary,
  AdminUsersFilters,
  PaginatedUsersResponse,
  UserRole,
  SubscriptionTier,
  UserRoleChangeRequest,
  UserSubscriptionChangeRequest
} from '../types/api';
import './UserManagement.css';

const UserManagement: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const user = useAppSelector(selectUser);

  const [isLoading, setIsLoading] = useState(true);
  const [users, setUsers] = useState<UserSummary[]>([]);
  const [pagination, setPagination] = useState({
    total: 0,
    page: 1,
    size: 20,
    pages: 0
  });
  const [filters, setFilters] = useState<AdminUsersFilters>({
    page: 1,
    size: 20
  });
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedUser, setSelectedUser] = useState<UserSummary | null>(null);
  const [showRoleModal, setShowRoleModal] = useState(false);
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
  const [actionLoading, setActionLoading] = useState<string | null>(null);

  useEffect(() => {
    const checkAdminAccess = async () => {
      try {
        const isAdmin = await apiClient.verifyAdminAccess();
        if (!isAdmin) {
          navigate('/dashboard');
          return;
        }
        await loadUsers();
      } catch (error) {
        console.error('Error checking admin access:', error);
        navigate('/dashboard');
      }
    };

    checkAdminAccess();
  }, [navigate]);

  useEffect(() => {
    loadUsers();
  }, [filters]);

  const loadUsers = async () => {
    try {
      setIsLoading(true);
      setError(null);

      const response: PaginatedUsersResponse = await apiClient.getAdminUsers(filters);
      setUsers(response.items || []);
      setPagination({
        total: response.total || 0,
        page: response.page || 1,
        size: response.size || 20,
        pages: response.pages || 1
      });
    } catch (error: any) {
      console.error('Error loading users:', error);
      setError('Failed to load users');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setFilters(prev => ({
      ...prev,
      search: searchQuery || undefined,
      page: 1
    }));
  };

  const handleFilterChange = (key: keyof AdminUsersFilters, value: any) => {
    setFilters(prev => ({
      ...prev,
      [key]: value,
      page: 1
    }));
  };

  const handlePageChange = (newPage: number) => {
    setFilters(prev => ({
      ...prev,
      page: newPage
    }));
  };

  const handleUserAction = async (action: string, userId: string) => {
    try {
      setActionLoading(`${action}-${userId}`);
      setError(null);

      switch (action) {
        case 'activate':
          await apiClient.activateUser(userId);
          break;
        case 'deactivate':
          await apiClient.deactivateUser(userId);
          break;
      }

      await loadUsers();
    } catch (error: any) {
      console.error(`Error ${action} user:`, error);
      setError(`Failed to ${action} user`);
    } finally {
      setActionLoading(null);
    }
  };

  const handleRoleChange = async (newRole: UserRole) => {
    if (!selectedUser) return;

    try {
      setActionLoading(`role-${selectedUser.user_id}`);
      setError(null);

      const request: UserRoleChangeRequest = { new_role: newRole };
      await apiClient.changeUserRole(selectedUser.user_id, request);

      setShowRoleModal(false);
      setSelectedUser(null);
      await loadUsers();
    } catch (error: any) {
      console.error('Error changing user role:', error);
      setError('Failed to change user role');
    } finally {
      setActionLoading(null);
    }
  };

  const handleSubscriptionChange = async (planName: string) => {
    if (!selectedUser) return;

    try {
      setActionLoading(`subscription-${selectedUser.user_id}`);
      setError(null);

      const request: UserSubscriptionChangeRequest = { plan_name: planName };
      await apiClient.changeUserSubscription(selectedUser.user_id, request);

      setShowSubscriptionModal(false);
      setSelectedUser(null);
      await loadUsers();
    } catch (error: any) {
      console.error('Error changing user subscription:', error);
      setError('Failed to change user subscription');
    } finally {
      setActionLoading(null);
    }
  };

  const handleLogout = () => {
    dispatch(logout());
    navigate('/');
  };

  const getRoleColor = (role: UserRole) => {
    switch (role) {
      case 'super_admin': return '#dc2626';
      case 'admin': return '#ea580c';
      case 'user': return '#059669';
      default: return '#6b7280';
    }
  };

  const getSubscriptionColor = (tier: string) => {
    switch (tier) {
      case 'premium': return '#7c3aed';
      case 'free': return '#6b7280';
      default: return '#6b7280';
    }
  };

  return (
    <div className="user-management">
      <header className="admin-header">
        <div className="header-content">
          <div>
            <h1>User Management</h1>
            <p>Manage users, roles, and subscriptions</p>
          </div>
          <div className="header-actions">
            <ThemeToggle />
            <Link to="/admin" className="btn btn-outline">
              Back to Admin
            </Link>
            <button className="btn btn-secondary" onClick={handleLogout}>
              Logout
            </button>
          </div>
        </div>
      </header>

      <main className="user-management-main">
        {error && (
          <div className="alert alert-error">
            {error}
            <button
              className="alert-retry"
              onClick={loadUsers}
            >
              Retry
            </button>
          </div>
        )}

        {/* Filters and Search */}
        <section className="filters-section">
          <form onSubmit={handleSearch} className="search-form">
            <input
              type="text"
              placeholder="Search users by email, username, or name..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="search-input"
            />
            <button type="submit" className="btn btn-primary">Search</button>
          </form>

          <div className="filters-row">
            <select
              value={filters.role_filter || ''}
              onChange={(e) => handleFilterChange('role_filter', e.target.value || undefined)}
              className="filter-select"
            >
              <option value="">All Roles</option>
              <option value="user">User</option>
              <option value="admin">Admin</option>
              <option value="super_admin">Super Admin</option>
            </select>

            <select
              value={filters.status_filter || ''}
              onChange={(e) => handleFilterChange('status_filter', e.target.value || undefined)}
              className="filter-select"
            >
              <option value="">All Status</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>

            <select
              value={filters.subscription_filter || ''}
              onChange={(e) => handleFilterChange('subscription_filter', e.target.value || undefined)}
              className="filter-select"
            >
              <option value="">All Subscriptions</option>
              <option value="free">Free</option>
              <option value="pro">Pro</option>
              <option value="premium">Premium</option>
            </select>

            <button
              onClick={() => {
                setFilters({ page: 1, size: 20 });
                setSearchQuery('');
              }}
              className="btn btn-outline btn-sm"
            >
              Clear Filters
            </button>
          </div>
        </section>

        {/* Users Table */}
        <section className="users-section">
          <div className="users-header">
            <h2>Users ({pagination.total.toLocaleString()})</h2>
          </div>

          {isLoading ? (
            <div className="loading-container">
              <LoadingSpinner size="lg" text="Loading users..." />
            </div>
          ) : (
            <>
              <div className="users-table-container">
                <table className="users-table">
                  <thead>
                    <tr>
                      <th>User</th>
                      <th>Role</th>
                      <th>Subscription</th>
                      <th>Status</th>
                      <th>Usage</th>
                      <th>Joined</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.map(user => (
                      <tr key={user.user_id}>
                        <td>
                          <div className="user-info">
                            <div className="user-details">
                              <div className="user-name">
                                {user.full_name || user.username}
                              </div>
                              <div className="user-email">{user.email}</div>
                            </div>
                          </div>
                        </td>
                        <td>
                          <span
                            className="role-badge"
                            style={{ backgroundColor: getRoleColor(user.user_role) }}
                          >
                            {user.user_role.replace('_', ' ')}
                          </span>
                        </td>
                        <td>
                          <span
                            className="subscription-badge"
                            style={{ backgroundColor: getSubscriptionColor(user.subscription_tier) }}
                          >
                            {user.subscription_tier}
                          </span>
                        </td>
                        <td>
                          <span className={`status-badge ${user.is_active ? 'active' : 'inactive'}`}>
                            {user.is_active ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                        <td>
                          <div className="usage-info">
                            <div className="usage-item">
                              Chapters: {user.chapter_limit === -1 ? '∞' : `${user.chapter_limit}`}
                            </div>
                            <div className="usage-item">
                              AI: {user.ai_usage_count || 0}/{user.ai_usage_limit === -1 ? '∞' : user.ai_usage_limit}
                            </div>
                          </div>
                        </td>
                        <td>
                          {new Date(user.created_at).toLocaleDateString()}
                        </td>
                        <td>
                          <div className="actions-dropdown">
                            <button className="btn btn-sm btn-outline">Actions ▼</button>
                            <div className="dropdown-menu">
                              <Link
                                to={`/admin/users/${user.user_id}`}
                                className="dropdown-item"
                              >
                                View Details
                              </Link>
                              <button
                                className="dropdown-item"
                                onClick={() => {
                                  setSelectedUser(user);
                                  setShowRoleModal(true);
                                }}
                              >
                                Change Role
                              </button>
                              <button
                                className="dropdown-item"
                                onClick={() => {
                                  setSelectedUser(user);
                                  setShowSubscriptionModal(true);
                                }}
                              >
                                Change Subscription
                              </button>
                              <hr className="dropdown-divider" />
                              {user.is_active ? (
                                <button
                                  className="dropdown-item danger"
                                  onClick={() => handleUserAction('deactivate', user.user_id)}
                                  disabled={!!actionLoading}
                                >
                                  {actionLoading === `deactivate-${user.user_id}` ? 'Deactivating...' : 'Deactivate'}
                                </button>
                              ) : (
                                <button
                                  className="dropdown-item"
                                  onClick={() => handleUserAction('activate', user.user_id)}
                                  disabled={!!actionLoading}
                                >
                                  {actionLoading === `activate-${user.user_id}` ? 'Activating...' : 'Activate'}
                                </button>
                              )}
                            </div>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {pagination.pages > 1 && (
                <div className="pagination">
                  <button
                    className="btn btn-outline btn-sm"
                    onClick={() => handlePageChange(pagination.page - 1)}
                    disabled={pagination.page === 1}
                  >
                    Previous
                  </button>

                  <span className="pagination-info">
                    Page {pagination.page} of {pagination.pages}
                  </span>

                  <button
                    className="btn btn-outline btn-sm"
                    onClick={() => handlePageChange(pagination.page + 1)}
                    disabled={pagination.page === pagination.pages}
                  >
                    Next
                  </button>
                </div>
              )}
            </>
          )}
        </section>
      </main>

      {/* Role Change Modal */}
      {showRoleModal && selectedUser && (
        <div className="modal-overlay" onClick={() => setShowRoleModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Change User Role</h2>
              <button
                className="close-btn"
                onClick={() => setShowRoleModal(false)}
              >
                ×
              </button>
            </div>
            <div className="modal-content">
              <p>Change role for <strong>{selectedUser.email}</strong>:</p>
              <div className="role-options">
                {(['user', 'admin', 'super_admin'] as UserRole[]).map(role => (
                  <button
                    key={role}
                    className={`role-option ${selectedUser.user_role === role ? 'current' : ''}`}
                    onClick={() => handleRoleChange(role)}
                    disabled={!!actionLoading || selectedUser.user_role === role}
                  >
                    {role.replace('_', ' ')}
                    {selectedUser.user_role === role && ' (Current)'}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Subscription Change Modal */}
      {showSubscriptionModal && selectedUser && (
        <div className="modal-overlay" onClick={() => setShowSubscriptionModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Change Subscription</h2>
              <button
                className="close-btn"
                onClick={() => setShowSubscriptionModal(false)}
              >
                ×
              </button>
            </div>
            <div className="modal-content">
              <p>Change subscription for <strong>{selectedUser.email}</strong>:</p>
              <div className="subscription-options">
                {(['free', 'pro', 'premium'] as SubscriptionTier[]).map(tier => (
                  <button
                    key={tier}
                    className={`subscription-option ${selectedUser.subscription_tier === tier ? 'current' : ''}`}
                    onClick={() => handleSubscriptionChange(tier)}
                    disabled={!!actionLoading || selectedUser.subscription_tier === tier}
                  >
                    {tier.charAt(0).toUpperCase() + tier.slice(1)}
                    {selectedUser.subscription_tier === tier && ' (Current)'}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserManagement;